import React, { useState, useContext, useEffect } from "react";
import axios from "../../axiosConfig";
import { TrainerContext } from "../../context/TrainerContext";
import "../../styles/AccountDetails.css";

const TrainerAccountDetails = () => {
    const { trainer, updateTrainerWithNewData } = useContext(TrainerContext);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: {
            country: '',
            city: '',
            postalCode: '',
            street: '',
            buildingNumber: '',
            apartmentNumber: ''
        }
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);

    // Initialize form data when trainer data is loaded
    useEffect(() => {
        if (trainer) {
            setFormData({
                firstName: trainer.firstName || '',
                lastName: trainer.lastName || '',
                email: trainer.email || '',
                phone: trainer.phone || '',
                address: {
                    country: trainer.address?.country || '',
                    city: trainer.address?.city || '',
                    postalCode: trainer.address?.postalCode || '',
                    street: trainer.address?.street || '',
                    buildingNumber: trainer.address?.buildingNumber || '',
                    apartmentNumber: trainer.address?.apartmentNumber || ''
                }
            });
        }
    }, [trainer]);

    if (!trainer) return <div className="trainer-account-tab__loading">Ładowanie...</div>;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            address: {
                ...prevData.address,
                [name]: value
            }
        }));
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Reset form data to original trainer data when canceling
            setFormData({
                firstName: trainer.firstName || '',
                lastName: trainer.lastName || '',
                email: trainer.email || '',
                phone: trainer.phone || '',
                address: {
                    country: trainer.address?.country || '',
                    city: trainer.address?.city || '',
                    postalCode: trainer.address?.postalCode || '',
                    street: trainer.address?.street || '',
                    buildingNumber: trainer.address?.buildingNumber || '',
                    apartmentNumber: trainer.address?.apartmentNumber || ''
                }
            });
            setError('');
            setSuccessMessage('');
        }
        setIsEditing(!isEditing);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        // Ensure address object exists and has the right structure
        const updateData = {
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            email: formData.email || '',
            phone: formData.phone || '',
            address: {
                country: formData.address?.country || '',
                city: formData.address?.city || '',
                postalCode: formData.address?.postalCode || '',
                street: formData.address?.street || '',
                buildingNumber: formData.address?.buildingNumber || '',
                apartmentNumber: formData.address?.apartmentNumber || ''
            }
        };

        try {
            await axios.put('/api/v1/trainer/update', updateData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            await updateTrainerWithNewData();
            setIsEditing(false);
            setSuccessMessage('Zmiany zapisane pomyślnie!');
        } catch (err) {
            console.error('Error updating trainer:', err);
            setError('Nie udało się zapisać zmian.');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleChangePasswordSubmit = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        if (passwordData.newPassword !== passwordData.confirmNewPassword) {
            setError('Nowe hasła nie są zgodne.');
            setLoading(false);
            return;
        }

        try {
            await axios.put('/api/v1/auth/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            setSuccessMessage('Hasło zostało zmienione pomyślnie!');
            setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
            setIsChangingPassword(false);
        } catch (err) {
            setError('Nie udało się zmienić hasła. Sprawdź poprawność starego hasła.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="trainer-account-tab">
            {/* Personal Information Section */}
            <div className="trainer-account-tab__section">
                <h3 className="trainer-account-tab__section-title">Informacje osobiste</h3>

                <div className="trainer-account-tab__row trainer-account-tab__row--personal-info">
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Imię</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="firstName"
                            value={formData.firstName || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            placeholder="Imię"
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Nazwisko</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="lastName"
                            value={formData.lastName || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            placeholder="Nazwisko"
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">E-mail</label>
                        <input
                            className="trainer-account-tab__input"
                            type="email"
                            name="email"
                            value={formData.email || ''}
                            disabled
                            placeholder="E-mail"
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Telefon</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="phone"
                            value={formData.phone || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            placeholder="Telefon"
                        />
                    </div>
                </div>
            </div>

            {/* Address Section */}
            <div className="trainer-account-tab__section">
                <h3 className="trainer-account-tab__section-title">Adres</h3>

                {/* First row: Country, Postal Code, City */}
                <div className="trainer-account-tab__address-row-1">
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Kraj</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="country"
                            value={formData.address?.country || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder="Kraj"
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Kod pocztowy</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="postalCode"
                            value={formData.address?.postalCode || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder="Kod pocztowy"
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Miasto</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="city"
                            value={formData.address?.city || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder="Miasto"
                        />
                    </div>
                </div>

                {/* Second row: Street, Building Number, Apartment Number */}
                <div className="trainer-account-tab__address-row-2">
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Ulica</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="street"
                            value={formData.address?.street || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder="Ulica"
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Numer budynku</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="buildingNumber"
                            value={formData.address?.buildingNumber || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder="Numer budynku"
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">Numer mieszkania</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="apartmentNumber"
                            value={formData.address?.apartmentNumber || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder="Numer mieszkania"
                        />
                    </div>
                </div>
            </div>

            {/* Messages */}
            {error && <div className="trainer-account-tab__error-message">{error}</div>}
            {successMessage && <div className="trainer-account-tab__success-message">{successMessage}</div>}

            {/* Action Buttons - Main actions and password change in one row */}
            <div className="trainer-account-tab__button-group">
                <button className="trainer-account-tab__btn trainer-account-tab__btn--secondary" onClick={handleEditToggle}>
                    {isEditing ? "Anuluj" : "Edytuj"}
                </button>
                {isEditing && (
                    <button className="trainer-account-tab__btn trainer-account-tab__btn--primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Zapisywanie..." : "Zapisz"}
                    </button>
                )}
                {!isChangingPassword && (
                    <button className="trainer-account-tab__btn trainer-account-tab__btn--outline" onClick={() => setIsChangingPassword(true)}>
                        Zmień hasło
                    </button>
                )}
            </div>

            {/* Password Change Section */}
            {isChangingPassword && (
                <div className="trainer-account-tab__section trainer-account-tab__password-section">
                    <h3 className="trainer-account-tab__section-title">Zmiana hasła</h3>
                    <div className="trainer-account-tab__row">
                        <div className="trainer-account-tab__field-group">
                            <label className="trainer-account-tab__label">Stare hasło</label>
                            <input
                                className="trainer-account-tab__input"
                                type="password"
                                name="currentPassword"
                                value={passwordData.currentPassword}
                                onChange={handlePasswordChange}
                                placeholder="Stare hasło"
                            />
                        </div>
                        <div className="trainer-account-tab__field-group">
                            <label className="trainer-account-tab__label">Nowe hasło</label>
                            <input
                                className="trainer-account-tab__input"
                                type="password"
                                name="newPassword"
                                value={passwordData.newPassword}
                                onChange={handlePasswordChange}
                                placeholder="Nowe hasło"
                            />
                        </div>
                        <div className="trainer-account-tab__field-group">
                            <label className="trainer-account-tab__label">Powtórz nowe hasło</label>
                            <input
                                className="trainer-account-tab__input"
                                type="password"
                                name="confirmNewPassword"
                                value={passwordData.confirmNewPassword}
                                onChange={handlePasswordChange}
                                placeholder="Powtórz nowe hasło"
                            />
                        </div>
                    </div>
                    <div className="trainer-account-tab__button-group">
                        <button className="trainer-account-tab__btn trainer-account-tab__btn--secondary" onClick={() => setIsChangingPassword(false)}>
                            Anuluj
                        </button>
                        <button className="trainer-account-tab__btn trainer-account-tab__btn--primary" onClick={handleChangePasswordSubmit} disabled={loading}>
                            {loading ? "Zapisywanie..." : "Zapisz hasło"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainerAccountDetails;